import { AvailiableLocales, Localization } from '@workday/i18n/nextgen';
import { useIsMounted } from '@workday/wcp-cargo-react';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { Audience } from 'shared/models/auth';
import { Region } from 'shared/models/regions';
import { SessionInfo } from 'shared/models/session';
import { TOGGLE_LANGUAGE_SELECTOR } from 'config/toggles';
import { session } from '../auth/session';
import { isAvailableLanguageOption } from '../components/DevsiteI18nProvider';

export const defaultSessionContext: SessionContextModel = {
  info: {
    scope: {
      // eslint-disable-next-line camelcase -- external API contract uses snake_case
      region_fqdn: '',
      region: '',
      // eslint-disable-next-line camelcase -- external API contract uses snake_case
      client_id: '',
    },
    exp: 0,
    sub: '',
    regionDisplayName: '',
    regionAlias: '',
    authBaseUri: '',
    csrfToken: '',
    platform: Audience.ENTERPRISE,
  },
  isSignedIn: () => false,
  isOpenDeveloper: false,
  isConnectorsRestrictedRegion: false,
};

/** Regions where features such as Connectors are not offered. */
const CONNECTORS_RESTRICTED_REGION: string[] = [Region.FEDERAL, Region.EUSC, Region.IL4];

export interface SessionContextModel {
  info: SessionInfo;
  isSignedIn: () => boolean;
  isOpenDeveloper: boolean;
  isConnectorsRestrictedRegion: boolean;
  userLocale?: Localization;
  setUserLocale?: (locale: AvailiableLocales) => void;
}

export const SessionContext = React.createContext<SessionContextModel>(defaultSessionContext);

export const SessionProvider: React.FC<React.PropsWithChildren> = (props) => {
  // Sets the language defaults using a rolling fallback (local storage -> system language (from the browser) -> 'en')
  // The recommendation from the #ui-i18n-tools team is to always use `en.json` as the default.
  const defaultLanguageId = 'en';
  const systemLanguageId = localStorage.getItem('userLocale') || navigator?.language?.replace('-', '_');
  const languageId = TOGGLE_LANGUAGE_SELECTOR
    ? isAvailableLanguageOption(systemLanguageId as AvailiableLocales) || defaultLanguageId
    : defaultLanguageId;
  // @ts-expect-error DEVEXTOOLS-1514: fix strictNullChecks rule violation
  const [sessionInfo, setSessionInfo] = useState<SessionInfo>(null);
  const [userLocale, setUserLocale] = useState<Localization>({
    language: languageId as AvailiableLocales,
    locale: languageId as AvailiableLocales,
  });

  const callIfMounted = useIsMounted();

  const updateSessionInfo = (info: SessionInfo) => {
    callIfMounted(() => setSessionInfo(info));
  };

  useEffect(() => {
    session
      .getInfo()
      .then(updateSessionInfo)
      .catch(() => {
        updateSessionInfo(defaultSessionContext.info);
      });

    // listen for session updates to refresh the session info broadcasted.
    const listenerId = session.addSessionChangeListener(updateSessionInfo);

    return () => {
      session.removeSessionChangeListener(listenerId);
    };
  }, []);

  if (!sessionInfo) return null;

  const value = {
    info: sessionInfo,
    isSignedIn: () => Boolean(sessionInfo?.exp && Date.now() / 1000 < sessionInfo.exp),
    isOpenDeveloper: sessionInfo.platform === Audience.OPEN_DEVELOPER,
    isConnectorsRestrictedRegion: CONNECTORS_RESTRICTED_REGION.includes(sessionInfo.scope.region),
    // TODO: WCPX-9327 Refactor so it relies on the User Preferences API and not just localStorage
    userLocale,
    setUserLocale: (locale: AvailiableLocales) => {
      if (TOGGLE_LANGUAGE_SELECTOR) {
        localStorage.setItem('userLocale', locale);
        setUserLocale({
          language: locale,
          locale: locale.slice(0, 2) as AvailiableLocales,
        });
      }
    },
  };

  return <SessionContext.Provider value={value}>{props.children}</SessionContext.Provider>;
};
